import { showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import { useMutation } from '@tanstack/react-query'
import { getQueryKey, useQuery } from '~/services'
import { queryClient } from '../../../providers/ReactQuery'
import { BillingApiClient } from '../services'
import { TrialPlan } from '../types'

export const useFreeTrial = (workspaceId: string) => {
  const { data, isLoading, error } = useQuery('workspaces_/$workspaceId_/billing/trials', { workspaceId })

  const trials = data?.trials ?? []
  return {
    trials,
    isLoading,
    error,
  }
}

export const useActiveTrial = (workspaceId: string) => {
  const { data } = useQuery('workspaces_/$workspaceId_/billing/trials', { workspaceId })
  return data?.trials.find((trial) => trial.isActive && trial.workspaceId === workspaceId)
}

export const useCreateFreeTrial = (workspaceId: string) => {
  return useMutation({
    mutationFn: async (trialPlan: TrialPlan) => await BillingApiClient.createTrial(workspaceId, trialPlan),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_'),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/billing/trials', { workspaceId }),
        }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes(workspaceId) && query.queryKey.includes('usage'),
        }),
      ])
      showSuccessToast('Free trial enabled successfully')
    },
    onError: (error) => {
      showErrorToast(`There was an error while enabling your free trial: ${error.message}`)
    },
  })
}

export const useCancelFreeTrial = (workspaceId: string) => {
  return useMutation({
    mutationFn: async () => await BillingApiClient.removeActiveTrial(workspaceId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_'),
        }),
        queryClient.invalidateQueries({
          queryKey: getQueryKey('workspaces_/$workspaceId_/billing/trials', { workspaceId }),
        }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes(workspaceId) && query.queryKey.includes('usage'),
        }),
      ])
      showSuccessToast('Free trial cancelled successfully')
    },
    onError: (error) => {
      showErrorToast(`There was an error while cancelling your free trial: ${error.message}`)
    },
  })
}
